import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { serverErrorText } from "./serverError";
import { StoredTemplateInvalidAlert } from "./StoredTemplateInvalidAlert";
import { TemplateField, isTemplateSubmittable } from "./TemplateField";
import { TemplateLegend } from "./TemplateLegend";
import { useOrgTemplate } from "./useOrgTemplate";
import { useResetOrgTemplate } from "./useResetOrgTemplate";
import { useSetOrgTemplate } from "./useSetOrgTemplate";
import type { BranchTemplateSettingsResponse } from "./api";

// Секция org-дефолта: показывает фактический шаблон и его источник
// (override человека или заводской дефолт), поле ввода с легендой и
// пресетами, кнопки «Сохранить» (PUT) и «Сбросить на заводской»
// (DELETE). Источник правды по пресетам/плейсхолдерам — ответ API.
// Если сохранённый org-дефолт перестал проходить правила записи, под
// строкой фактического шаблона показывается плашка с причиной.
export function OrgTemplateSection(): ReactElement {
  const { t } = useTranslation();
  const settings = useOrgTemplate();

  if (settings.isPending) {
    return <Loader />;
  }
  if (settings.isError) {
    return (
      <Alert color="red" title={t("branchNameTemplate.errorHeading")}>
        {t("branchNameTemplate.loadingError")}
      </Alert>
    );
  }
  // `key` по сохранённому и фактическому значению: после save/reset
  // сервер отдаёт новое состояние → тело перемонтируется и draft
  // переинициализируется им, без setState-в-effect. Одного фактического
  // шаблона мало: при невалидном сохранённом значении он заводской и
  // после сброса не меняется, а черновик остался бы со сломанным текстом.
  // `JSON.stringify` не склеивает `null` и пустую строку (сохранённое
  // значение может быть пустым), поэтому переход между ними перемонтирует.
  return (
    <OrgTemplateBody
      key={JSON.stringify([
        settings.data.org_template,
        settings.data.effective_org_template,
      ])}
      settings={settings.data}
    />
  );
}

// Источник шаблона для бейджа: откат на заводской при невалидном
// сохранённом значении, иначе override организации или заводской дефолт.
function sourceBadge(settings: BranchTemplateSettingsResponse): {
  readonly color: string;
  readonly labelKey: string;
} {
  if (settings.org_template_invalid_reason !== null) {
    return {
      color: "orange",
      labelKey: "branchNameTemplate.org.sourceFallback",
    };
  }
  if (settings.org_template !== null) {
    return { color: "blue", labelKey: "branchNameTemplate.org.sourceOverride" };
  }
  return { color: "gray", labelKey: "branchNameTemplate.org.sourceDefault" };
}

// Тело секции с загруженными настройками. Локальный draft шаблона
// инициализируется сохранённым org-значением, а без него — фактическим;
// при невалидном сохранённом значении черновик — его текст для правки
// (перемонтаж по `key` после save/reset подтягивает новое состояние).
function OrgTemplateBody({
  settings,
}: {
  readonly settings: BranchTemplateSettingsResponse;
}): ReactElement {
  const { t } = useTranslation();
  const setMutation = useSetOrgTemplate();
  const resetMutation = useResetOrgTemplate();
  const [draft, setDraft] = useState(
    settings.org_template ?? settings.effective_org_template,
  );

  const isOverride = settings.org_template !== null;
  const invalidReason = settings.org_template_invalid_reason;
  const badge = sourceBadge(settings);
  const submittable = isTemplateSubmittable(draft, settings.placeholders);
  const mutationError = setMutation.error ?? resetMutation.error;
  const serverError =
    mutationError !== null ? serverErrorText(mutationError, t) : null;

  return (
    <Stack gap="md">
      <Title order={2}>{t("branchNameTemplate.org.title")}</Title>
      <Text c="dimmed">{t("branchNameTemplate.org.intro")}</Text>

      <Group gap="xs">
        <Text size="sm" fw={600}>
          {t("branchNameTemplate.org.currentLabel")}
        </Text>
        <Text size="sm" ff="monospace">
          {settings.effective_org_template}
        </Text>
        <Badge
          variant="light"
          color={badge.color}
          aria-label={t("branchNameTemplate.org.sourceLabel")}
        >
          {t(badge.labelKey)}
        </Badge>
      </Group>

      {invalidReason !== null ? (
        <StoredTemplateInvalidAlert
          storedTemplate={settings.org_template}
          reason={invalidReason}
          fallbackNote={t("branchNameTemplate.org.invalidFallback", {
            template: settings.effective_org_template,
          })}
        />
      ) : null}

      <TemplateField
        value={draft}
        onChange={setDraft}
        presets={settings.presets}
        placeholders={settings.placeholders}
        label={t("branchNameTemplate.org.fieldLabel")}
        serverError={serverError}
        legendSlot={<TemplateLegend placeholders={settings.placeholders} />}
      />

      <Group>
        <Button
          onClick={() => {
            setMutation.mutate({ template: draft.trim() });
          }}
          disabled={!submittable}
          loading={setMutation.isPending}
        >
          {t("branchNameTemplate.org.saveButton")}
        </Button>
        <Button
          variant="default"
          onClick={() => {
            resetMutation.mutate();
          }}
          disabled={!isOverride}
          loading={resetMutation.isPending}
        >
          {t("branchNameTemplate.org.resetButton")}
        </Button>
      </Group>
    </Stack>
  );
}
